import type { GetTravelerInformationDataQuery } from '@eti/schema-types';
import { travelerForm } from '@eti/travelers';
import type { CountryType } from '../../../../common/utils/models';
import { contactFields, contactTypes } from '../../../traveler-details/constants/formFields';

const {
  EMERGENCY_LAST_NAME,
  EMERGENCY_PHONE,
  EMERGENCY_PHONE_COUNTRY_ID,
  EMAIL,
  EMAIL_CONFIRM,
  NEWSLETTER,
  PHONE,
  PHONE_COUNTRY_ID,
} = contactFields;

const { TRAVELER } = contactTypes;

const {
  DATE_OF_BIRTH,
  FIRST_NAME,
  FREQUENT_FLYER,
  FREQUENT_FLYER_CARD_NUMBER,
  FREQUENT_FLYER_CARD_TYPE,
  GENDER,
  LAST_NAME,
  TRAVEL_DOCUMENT_EXPIRY_DATE,
  TRAVEL_DOCUMENT_ISSUING_COUNTRY,
  TRAVEL_DOCUMENT_NUMBER,
} = travelerForm;

type FrequentFlyerKeysType =
  | `${typeof FREQUENT_FLYER_CARD_TYPE}`
  | `${typeof FREQUENT_FLYER_CARD_NUMBER}`;

type TravelerSectionFrequentFlyerType = Partial<
  Record<typeof FREQUENT_FLYER, Record<FrequentFlyerKeysType, string | number | { value: string }>>
>;

type TravelerSectionCommonKeysTypes =
  | `${typeof GENDER}-${string}`
  | `${typeof FIRST_NAME}-${string}`
  | `${typeof LAST_NAME}-${string}`
  | `${typeof DATE_OF_BIRTH}-${string}`
  | `${typeof TRAVEL_DOCUMENT_ISSUING_COUNTRY}-${string}`
  | `${typeof TRAVEL_DOCUMENT_EXPIRY_DATE}-${string}`
  | `${typeof TRAVEL_DOCUMENT_NUMBER}-${string}`;

type TravelerSectionCommonType = Record<
  TravelerSectionCommonKeysTypes,
  string | CountryType | undefined
>;

type ContactInformationsKeysType =
  | typeof EMAIL
  | typeof EMAIL_CONFIRM
  | typeof PHONE
  | typeof PHONE_COUNTRY_ID
  | typeof EMERGENCY_PHONE
  | typeof EMERGENCY_PHONE_COUNTRY_ID
  | typeof EMERGENCY_LAST_NAME
  | typeof NEWSLETTER;

export type ContactInformationsType = Partial<
  Record<ContactInformationsKeysType, string | number | boolean>
>;

export type TravelerSectionKeyType = `${typeof TRAVELER}-section-${string}`;

export type TravelerSectionType = TravelerSectionCommonType & TravelerSectionFrequentFlyerType;

export const transformTravelerInformationQueryToFormShape = (
  travelerInformationQuery: GetTravelerInformationDataQuery,
  siteContextMarket: CountryType,
) => {
  // TODO: Change after schema is fixed
  let transformedTravelers: Record<
    TravelerSectionKeyType | 'contactInformation',
    TravelerSectionType | ContactInformationsType
  > = {
    contactInformation: {},
  };
  const contactInformations: ContactInformationsType = {
    [EMAIL]: travelerInformationQuery?.travelerInformation?.contactInformation.email ?? '',
    [EMAIL_CONFIRM]: travelerInformationQuery?.travelerInformation?.contactInformation.email ?? '',
    [PHONE]:
      travelerInformationQuery?.travelerInformation?.contactInformation.cellPhone
        .phoneNumberAfterPrefix ?? '',
    [PHONE_COUNTRY_ID]:
      Number(
        travelerInformationQuery?.travelerInformation?.contactInformation.cellPhone.countryId,
      ) ?? siteContextMarket.id,
    [EMERGENCY_PHONE]:
      travelerInformationQuery?.travelerInformation?.emergencyContactInformation?.phoneNumber
        ?.phoneNumberAfterPrefix ?? '',
    [EMERGENCY_PHONE_COUNTRY_ID]:
      Number(
        travelerInformationQuery?.travelerInformation?.emergencyContactInformation?.phoneNumber
          ?.countryId,
      ) ?? siteContextMarket.id,
    [EMERGENCY_LAST_NAME]:
      travelerInformationQuery?.travelerInformation?.emergencyContactInformation?.lastName ?? '',
    [NEWSLETTER]: travelerInformationQuery?.travelerInformation?.subscribeToNewsletter ?? false,
  };

  travelerInformationQuery?.travelerInformation?.travelers.forEach((traveler) => {
    const travelerSectionData: TravelerSectionType = {
      [`${GENDER}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]: traveler?.gender,
      [`${FIRST_NAME}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]:
        traveler?.firstName,
      [`${LAST_NAME}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]: traveler?.lastName,
      [`${DATE_OF_BIRTH}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]:
        traveler?.birthDate,
      [`${TRAVEL_DOCUMENT_ISSUING_COUNTRY}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]:
        siteContextMarket ?? {},
      [`${TRAVEL_DOCUMENT_EXPIRY_DATE}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]:
        traveler?.travelDocumentInformation?.expiryDate,
      [`${TRAVEL_DOCUMENT_NUMBER}-${traveler?.id}` satisfies TravelerSectionCommonKeysTypes]:
        traveler?.travelDocumentInformation?.travelDocumentNumber,

      // Frequent flyer
      ...(traveler?.frequentFlyerCard?.frequentFlyerCardNumber &&
        traveler?.frequentFlyerCard?.frequentFlyerCardId && {
          [FREQUENT_FLYER]: {
            [`${FREQUENT_FLYER_CARD_TYPE}` satisfies FrequentFlyerKeysType]:
              traveler?.frequentFlyerCard.frequentFlyerCardId.toString(),
            [`${FREQUENT_FLYER_CARD_NUMBER}` satisfies FrequentFlyerKeysType]:
              traveler?.frequentFlyerCard.frequentFlyerCardNumber,
          },
        }),
    };

    const travelerSection: Record<TravelerSectionKeyType, TravelerSectionType> = {
      [`${TRAVELER}-section-${traveler?.id}`]: travelerSectionData,
    };

    const contactInformationSection = {
      contactInformation: contactInformations,
    };

    transformedTravelers = {
      ...transformedTravelers,
      ...travelerSection,
      ...contactInformationSection,
    };
  });

  return transformedTravelers;
};
